import { useCallback } from "react";

import { useTranslationSelector } from "hooks/useSelector";

function findValueByKey(
  obj, // The object to search within
  targetKey, // The key whose value we want to find
  excludeKeys = ["setting_categories"], // Keys to exclude from the search
  includeKeys = [], // Keys to explicitly include in the search
  fixedParentKey = null // A fixed parent key to match against
) {
  // If the input is not an object, return undefined
  if (!obj || typeof obj !== "object") return undefined;

  // Initialize a stack for iterative depth-first search (DFS)
  const stack = [{ node: obj, parentKey: null }];

  // Loop until the stack is empty
  while (stack.length) {
    // Pop the last element from the stack
    const { node: current, parentKey } = stack.pop();

    // Check if the current object contains the target key
    if (current[targetKey] !== undefined) {
      // If fixedParentKey is specified, check if parentKey matches
      if (!!fixedParentKey) {
        if (parentKey === fixedParentKey) {
          return current[targetKey]; // Return immediately if parentKey matches
        } else {
          continue; // Skip if parentKey doesn't match fixedParentKey
        }
      }

      // If includeKeys is empty or the parentKey is in includeKeys, return the value
      if (
        includeKeys.length === 0 ||
        (parentKey && includeKeys.includes(parentKey))
      ) {
        return current[targetKey];
      }
    }

    // Iterate over the keys of the current object
    for (const key in current) {
      // If the value is an object, not null, and not in excludeKeys, add it to the stack
      if (
        typeof current[key] === "object" &&
        current[key] !== null &&
        !excludeKeys.includes(key)
      ) {
        stack.push({ node: current[key], parentKey: key });
      }
    }
  }

  // If no matching key is found, return undefined
  return undefined;
}

const useTitleTranslation = () => {
  const translation = useTranslationSelector();

  const getTitle = useCallback(
    (
      name = "",
      defaultValue = "",
      excludeKeys = ["setting_categories"],
      includeKeys = [], // Keys to explicitly include in the search
      fixedParentKey = null // A fixed parent key to match against
    ) =>
      findValueByKey(
        translation,
        name,
        excludeKeys,
        includeKeys,
        fixedParentKey
      ) || defaultValue,
    [translation]
  );

  return { getTitle };
};

export default useTitleTranslation;
